import React, { useEffect } from "react";
import LevelTile from "../components/recording/LevelTile";
import { useTranslation } from "react-i18next";
import { BreathingGraphSVG } from "../components/recording/BreathingGraphSVG";
import { trackEvent } from "../utils/analytics/track";
import HRChart from "../components/icons/HRChart";
import { RECORDING_MODES } from "../utils/recordingModes";
import Spinner from "../components/Spinner";
import { useNavigate } from "react-router-dom";
import { useActiveMeasurementSession } from "../recording/session/ActiveMeasurementSessionContext";

export default function LevelSelectionPage({
  firstRunByVariant,
  isPermitted,
  loadingModes,
}) {
  const { t } = useTranslation();

  const navigate = useNavigate();
  const { begin } = useActiveMeasurementSession();

  const startModeAndGo = (mode, path, analyticsEvent) => {
    begin(mode);
    if (analyticsEvent) trackEvent(analyticsEvent);
    navigate(path);
  };

  useEffect(() => {
    trackEvent("level_selection_view");
  }, []);

  if (loadingModes) return <Spinner />;

  const pulsePath = firstRunByVariant["RecordingPulseScan"]
    ? "/recording/pulse-scan/guide"
    : "/recording/pulse-scan/camera";

  const spirometryPath = firstRunByVariant["RecordingSpirometry"]
    ? "/recording/spirometry/guide"
    : "/recording/spirometry/camera";

  const tidalPath = firstRunByVariant["RecordingTidalBreathing"]
    ? "/recording/tidal-breathing/guide"
    : "/recording/tidal-breathing/camera";

  const manualAudioPath = firstRunByVariant["RecordingManualAudio"]
    ? "/recording/manual-audio/guide"
    : "/recording/manual-audio/camera";

  const postExertionPath = firstRunByVariant["RecordingPostExertion"]
    ? "/recording/post-exertion/guide"
    : "/recording/post-exertion/camera";

  return (
    <div className="w-full max-w-lg h-fit min-h-full p-4 flex flex-col justify-start items-stretch gap-4">
      {isPermitted(RECORDING_MODES.PULSE) && (
        <LevelTile
          to={pulsePath}
          title={t("levelSelection.pulseScanTitle")}
          desc={t("levelSelection.pulseScanDesc")}
          img={<HRChart className="w-full" />}
          onClick={(e) => {
            e?.preventDefault?.();
            startModeAndGo(
              "pulse-scan",
              pulsePath,
              "level_selection_click_pulse_scan",
            );
          }}
        />
      )}

      {isPermitted(RECORDING_MODES.MAUDIO) && (
        <LevelTile
          to={manualAudioPath}
          title={t("levelSelection.manualAudioTitle")}
          desc={t("levelSelection.manualAudioDesc")}
          img={
            <img
              src="/audio_chart.svg"
              alt=""
              loading="eager"
              className="h-24 w-full object-contain"
            />
          }
          onClick={(e) => {
            e?.preventDefault?.();
            startModeAndGo(
              "manual-audio",
              manualAudioPath,
              "level_selection_click_manual_audio",
            );
          }}
        />
      )}

      {isPermitted(RECORDING_MODES.SPIROMETRY) && (
        <LevelTile
          to={spirometryPath}
          title={t("levelSelection.spirometryTitle")}
          desc={t("levelSelection.spirometryDesc")}
          img={<BreathingGraphSVG percent={100} className="w-full" />}
          onClick={(e) => {
            e?.preventDefault?.();
            startModeAndGo(
              "spirometry",
              spirometryPath,
              "level_selection_click_spirometry",
            );
          }}
        />
      )}

      {isPermitted(RECORDING_MODES.TIDAL) && (
        <LevelTile
          to={tidalPath}
          title={t("levelSelection.tidalBreathingTitle")}
          desc={t("levelSelection.tidalBreathingDesc")}
          img={
            <img
              src="/breathing_graph_normal.svg"
              alt=""
              loading="eager"
              className="h-24 w-full object-contain"
            />
          }
          onClick={(e) => {
            e?.preventDefault?.();
            startModeAndGo(
              "tidal-breathing",
              tidalPath,
              "level_selection_click_tidal_breathing",
            );
          }}
        />
      )}

      {isPermitted(RECORDING_MODES.POSTEXERTION) && (
        <LevelTile
          to={postExertionPath}
          title={t("levelSelection.postExertionTitle")}
          desc={t("levelSelection.postExertionDesc")}
          img={
            <img
              src="/recovery_curve.svg"
              alt=""
              loading="eager"
              className="h-24 w-full object-contain"
            />
          }
          onClick={(e) => {
            e?.preventDefault?.();
            startModeAndGo(
              "post-exertion",
              postExertionPath,
              "level_selection_click_post_exertion",
            );
          }}
        />
      )}
    </div>
  );
}
